/**
 * MassagerListSection
 *
 * Organism: massager list card with table, action bar, messages, pagination.
 * No API calls; receives data and callbacks from page.
 */

import React, { useMemo } from 'react';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';
import { formatJapaneseDate } from '@/utils/dateFormat';
import { formatEndDateOrHide } from '@/utils/dateConverter';
import {
  AppButton,
  AppCheckbox,
  AppPagination,
  AppTable,
  AppTableActionButton,
  AppTooltip,
  AppSpin,
} from '@/components/atoms';
import type { MassagerClinicLocation } from '@/pages/main/massager/types';
import type { PaginationInfo } from '@/pages/main/massager/types';
import type { ColumnsType } from 'antd/es/table';
import { getAntdTableSortOrder } from '@/utils';

export interface MassagerListSectionProps {
  error: string | null;
  successMessage: string | null;
  onDismissSuccess: () => void;
  checkinEnabled: boolean;
  serviceType: string;
  massagers: MassagerClinicLocation[];
  pagination: PaginationInfo | null;
  selectedIds: Set<number>;
  onPageChange: (page: number) => void;
  onCheckboxToggle: (id: number) => void;
  onSelectAll: () => void;
  onDetailClick: (massager: MassagerClinicLocation) => void;
  onExportCsv: () => void;
  onOpenBulkSettings: () => void;
  exportCsvPending: boolean;
  /** when true shows a translucent overlay on top of the list (no layout shift) */
  loading?: boolean;
  isA2: boolean;
  numberColLabel: string;
  nameColLabel: string;
  addressColLabel: string;
  sortField?: string;
  sortOrder?: string;
  onSortChange?: (field: string | null, order: 'asc' | 'desc' | null) => void;
}

const MassagerListSection: React.FC<MassagerListSectionProps> = ({
  error,
  successMessage,
  onDismissSuccess,
  checkinEnabled,
  serviceType,
  massagers,
  pagination,
  selectedIds,
  onPageChange,
  onCheckboxToggle,
  onSelectAll,
  onDetailClick,
  onExportCsv,
  onOpenBulkSettings,
  exportCsvPending,
  loading = false,
  isA2,
  numberColLabel,
  nameColLabel,
  addressColLabel,
  sortField,
  sortOrder,
  onSortChange,
}) => {
  const { colors } = useTheme();

  const columns: ColumnsType<MassagerClinicLocation> = useMemo(() => {
    const cols: ColumnsType<MassagerClinicLocation> = [];
    if (checkinEnabled) {
      cols.push({
        key: 'checkbox',
        width: 56,
        align: 'center',
        title: (
          <AppCheckbox
            checked={selectedIds.size === massagers.length && massagers.length > 0}
            onChange={onSelectAll}
          />
        ),
        render: (_: unknown, record: MassagerClinicLocation) => (
          <AppCheckbox
            checked={selectedIds.has(record.id)}
            onChange={() => onCheckboxToggle(record.id)}
            onClick={e => e.stopPropagation()}
          />
        ),
      });
    }
    cols.push(
      {
        title: numberColLabel,
        dataIndex: ['massager', 'serial_number'],
        key: 'serial_number',
        width: 150,
        className: 'text-sm',
        sorter: true,
        sortOrder: getAntdTableSortOrder(sortField || '', 'serial_number', sortOrder as any),
      },
      {
        title: nameColLabel,
        dataIndex: ['massager', 'name'],
        key: 'name',
        width: 176,
        className: 'text-sm min-w-[11rem]',
        sorter: true,
        sortOrder: getAntdTableSortOrder(sortField || '', 'name', sortOrder as any),
        render: (_: unknown, record: MassagerClinicLocation) => (
          <AppTooltip title={record.massager?.name || ''} showOnlyOnOverflow>
            <div className="break-words line-clamp-2">{record.massager?.name || ''}</div>
          </AppTooltip>
        ),
      },
      {
        title: addressColLabel,
        dataIndex: 'address',
        key: 'address',
        width: 320,
        className: 'text-sm max-w-[20rem] min-w-0',
        sorter: true,
        sortOrder: getAntdTableSortOrder(sortField || '', 'address', sortOrder as any),
        render: (_: unknown, record: MassagerClinicLocation) => {
          const loc = record.clinic_location;
          const full = `${loc?.address1 ?? ''}${loc?.address2 ?? ''}${loc?.address3 ?? ''}`;
          return (
            <AppTooltip title={full} showOnlyOnOverflow>
              <div className="break-words line-clamp-2">{full}</div>
            </AppTooltip>
          );
        },
      }
    );
    if (isA2) {
      cols.push(
        {
          title: 'はり免許番号',
          dataIndex: ['massager', 'acupuncture_license_number'],
          key: 'acupuncture_license_number',
          width: 144,
          className: 'text-sm',
          render: (_: unknown, record: MassagerClinicLocation) => (
            <AppTooltip
              title={record.massager?.acupuncture_license_number || ''}
              showOnlyOnOverflow
            >
              <div className="break-words line-clamp-2">
                {record.massager?.acupuncture_license_number || ''}
              </div>
            </AppTooltip>
          ),
        },
        {
          title: 'きゅう免許番号',
          dataIndex: ['massager', 'moxibustion_license_number'],
          key: 'moxibustion_license_number',
          width: 144,
          className: 'text-sm',
          render: (_: unknown, record: MassagerClinicLocation) => (
            <AppTooltip
              title={record.massager?.moxibustion_license_number || ''}
              showOnlyOnOverflow
            >
              <div className="break-words line-clamp-2">
                {record.massager?.moxibustion_license_number || ''}
              </div>
            </AppTooltip>
          ),
        }
      );
    } else {
      cols.push({
        title: '免許番号',
        dataIndex: ['massager', 'license_number'],
        key: 'license_number',
        width: 160,
        className: 'text-sm',
        render: (_: unknown, record: MassagerClinicLocation) => (
          <AppTooltip title={record.massager?.license_number || ''} showOnlyOnOverflow>
            <div className="break-words line-clamp-2">{record.massager?.license_number || ''}</div>
          </AppTooltip>
        ),
      });
    }
    cols.push(
      {
        title: '適用開始日',
        dataIndex: 'available_from',
        key: 'available_from',
        width: 120,
        className: 'text-sm whitespace-nowrap',
        render: (_: unknown, record: MassagerClinicLocation) =>
          record.available_from ? formatJapaneseDate(record.available_from) : '',
      },
      {
        title: '適用終了日',
        dataIndex: 'available_to',
        key: 'available_to',
        width: 120,
        className: 'text-sm whitespace-nowrap',
        render: (_: unknown, record: MassagerClinicLocation) =>
          formatEndDateOrHide(record.available_to),
      },
      {
        title: '操作',
        key: 'detail',
        width: 80,
        align: 'center',
        render: (_: unknown, record: MassagerClinicLocation) => (
          <AppTableActionButton
            action="edit"
            onClick={e => {
              e.stopPropagation();
              onDetailClick(record);
            }}
          >
            編集
          </AppTableActionButton>
        ),
      }
    );
    return cols;
  }, [
    checkinEnabled,
    isA2,
    numberColLabel,
    nameColLabel,
    addressColLabel,
    massagers.length,
    selectedIds,
    onSelectAll,
    onCheckboxToggle,
    onDetailClick,
    sortField,
    sortOrder,
  ]);

  const tableScrollX = useMemo(() => {
    const total = columns.reduce(
      (sum, col) => sum + (typeof col.width === 'number' ? col.width : 120),
      0
    );
    return Math.max(total, 900);
  }, [columns]);

  const tableComponents = useMemo(
    () => ({
      header: {
        cell: (
          props: React.HTMLAttributes<HTMLTableCellElement> & { style?: React.CSSProperties }
        ) => (
          <th
            {...props}
            style={{
              ...props.style,
              backgroundColor: colors.sectionHeader,
              color: colors.sectionHeaderText,
            }}
          />
        ),
      },
    }),
    [colors.sectionHeader, colors.sectionHeaderText]
  );

  return (
    <div className="relative min-w-0 mb-3 bg-white rounded shadow card">
      <div className="min-w-0">
        {error && (
          <div className="px-4 py-3 mb-4 text-red-700 border border-red-200 rounded bg-red-50">
            {error}
          </div>
        )}

        {checkinEnabled && (
          <div className="mb-4 text-sm text-red-600">
            {serviceType === SERVICE_TYPE_A2
              ? '※ チェックイン機能をご利用される方は、施術者毎、「施術者ログインID」と「パスワード」を登録してください'
              : '※ チェックイン機能をご利用される方は、マッサージ師毎、「マッサージ師ログインID」と「パスワード」を登録してください'}
          </div>
        )}

        {successMessage && (
          <div className="flex items-center justify-between px-4 py-3 mb-4 text-green-700 border border-green-200 rounded bg-green-50">
            <span>{successMessage}</span>
            <AppButton
              type="text"
              onClick={onDismissSuccess}
              className="text-green-700 hover:text-green-900"
            >
              &times;
            </AppButton>
          </div>
        )}

        <div className="flex items-center justify-between gap-2 mb-4">
          <span className="text-sm text-gray-600">
            該当件数: <span className="font-medium">{pagination?.total ?? massagers.length}</span>件
          </span>
          <div className="flex gap-2">
            {checkinEnabled && (
              <AppButton
                type="primary"
                onClick={() => {
                  if (selectedIds.size === 0) {
                    alert('対象の行を選択してください。');
                    return;
                  }
                  onOpenBulkSettings();
                }}
                className="flex items-center gap-2"
              >
                <i className="fa fa-cog" />
                一括設定
                {selectedIds.size > 0 && (
                  <span className="bg-white text-blue-600 px-2 py-0.5 rounded-full text-xs">
                    {selectedIds.size}
                  </span>
                )}
              </AppButton>
            )}
            <AppButton
              type="primary"
              onClick={onExportCsv}
              disabled={exportCsvPending}
              className="flex items-center gap-2"
            >
              {exportCsvPending ? (
                <>
                  <span className="animate-spin">&#8987;</span>
                  出力中...
                </>
              ) : (
                <>
                  <i className="fa fa-download mr-1.5" />
                  CSV出力
                </>
              )}
            </AppButton>
          </div>
        </div>

        <div className="w-full max-w-full min-w-0 overflow-x-auto">
          <AppTable<MassagerClinicLocation>
            key={serviceType}
            columns={columns}
            dataSource={massagers}
            rowKey="id"
            pagination={false}
            scroll={{ x: tableScrollX }}
            tableLayout="fixed"
            components={tableComponents}
            onRow={record => ({
              onClick: () => checkinEnabled && onCheckboxToggle(record.id),
              className: `hover:bg-gray-50 ${
                checkinEnabled
                  ? `cursor-pointer ${selectedIds.has(record.id) ? 'bg-blue-50' : ''}`
                  : ''
              }`,
            })}
            onChange={(_pagination, _filters, sorter) => {
              if (loading) return;
              const sort = Array.isArray(sorter) ? sorter[0] : sorter;

              // Use columnKey (= the column's `key` prop) to map to API sort_field.
              // We also manage a 3-state toggle (asc → desc → none) ourselves
              // because AntD's internal order state does not always change when
              // sortOrder is fully controlled from the outside.
              const columnKey = sort?.columnKey ? String(sort.columnKey) : null;

              if (!columnKey) {
                if (onSortChange) onSortChange(null, null);
                return;
              }

              let nextField: string | null = columnKey;
              let nextOrder: 'asc' | 'desc' | null;

              if (!sortField || sortField !== columnKey || !sortOrder) {
                // First click on this column (or switching column) → ASC
                nextOrder = 'asc';
              } else if (sortOrder === 'asc') {
                // Second click on same column → DESC
                nextOrder = 'desc';
              } else {
                // Third click on same column → clear sort
                nextField = null;
                nextOrder = null;
              }

              if (onSortChange) {
                onSortChange(nextField, nextOrder);
              }
            }}
          />
        </div>

        {pagination && pagination.last_page > 1 && (
          <div className="flex justify-center mt-4">
            <AppPagination
              current={pagination.current_page}
              total={pagination.total}
              pageSize={pagination.per_page}
              onChange={onPageChange}
            />
          </div>
        )}
      </div>

      {/* translucent overlay shown while refetching so UI doesn't jump */}
      {loading && massagers.length > 0 && (
        <div
          className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-md bg-white/80"
          aria-busy="true"
        >
          <AppSpin size="large" />
        </div>
      )}
    </div>
  );
};

export default MassagerListSection;
